import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import '../../styles/LLOriginalPhotoSelection.css';

function PhotoSelection() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const allPhotos = state?.images || [];
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const canvasRef = useRef(null);

  const toggleSelect = (photo) => {
    if (selectedPhotos.includes(photo)) {
      setSelectedPhotos(prev => prev.filter(p => p !== photo));
    } else if (selectedPhotos.length < 4) {
      setSelectedPhotos(prev => [...prev, photo]);
    }
  };

  // Use the same rendering approach as ResultScreen
  useEffect(() => {
    if (!canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Set canvas dimensions based on frame aspect ratio (2400 x 3561)
    const frameAspectRatio = 3561 / 2400;
    canvas.width = 900;
    canvas.height = Math.round(canvas.width * frameAspectRatio);
    
    // Clear canvas with white background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Calculate photo dimensions based on frame size
    // Frame is 2400 x 3561px, photos are 1043 x 1430px
    const photoScale = 1043 / 2400; // Ratio of photo width to frame width
    
    // Calculate photo placement within the frame - these are fixed sizes in the frame
    const photoWidth = canvas.width * photoScale;
    const photoHeight = photoWidth * (1430 / 1043); // Maintain photo aspect ratio in frame
    
    // Define photo positions (percentages of frame dimensions)
    const photoPositions = [
      { x: 0.26, y: 0.26 },  // Top-left
      { x: 0.73, y: 0.26 },  // Top-right
      { x: 0.26, y: 0.68 },  // Bottom-left
      { x: 0.73, y: 0.68 }   // Bottom-right
    ];
    
    // Load frame first to ensure it exists
    const frameImg = new Image();
    frameImg.src = '/img/LoveFrame.svg';
    
    // Handle frame loading error by trying alternative paths
    frameImg.onerror = () => {
      console.log('Frame image failed to load at /img/LoveFrame.svg, trying alternative path');
      frameImg.src = 'img/LoveFrame.svg'; // Try without leading slash
      
      frameImg.onerror = () => {
        console.log('Frame image failed to load at img/LoveFrame.svg, trying another path');
        frameImg.src = '../img/LoveFrame.svg'; // Try relative path
        
        frameImg.onerror = () => {
          console.log('All frame image paths failed, drawing default frame');
          drawDefaultFrame();
          // Now draw photos on top of default frame
          drawPhotos();
        };
      };
    };
    
    // Draw default frame if needed
    const drawDefaultFrame = () => {
      // Draw default frame border
      ctx.strokeStyle = '#e0e0e0';
      ctx.lineWidth = 20;
      ctx.strokeRect(10, 10, canvas.width - 20, canvas.height - 20);
      
      // Add decorative elements
      ctx.fillStyle = '#ff6b6b';
      ctx.font = 'bold 48px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Photo Booth', canvas.width/2, 80);
    };
    
    // Helper function to calculate source crop dimensions for an image to fill target area
    const calculateCropDimensions = (imgWidth, imgHeight, targetWidth, targetHeight) => {
      const imgRatio = imgHeight / imgWidth;
      const targetRatio = targetHeight / targetWidth;
      
      let sourceX, sourceY, sourceWidth, sourceHeight;
      
      if (imgRatio > targetRatio) {
        // Image is taller than target - crop top and bottom
        sourceWidth = imgWidth;
        sourceHeight = imgWidth * targetRatio;
        sourceX = 0;
        sourceY = (imgHeight - sourceHeight) / 2; // Center the crop vertically
      } else {
        // Image is wider than target - crop left and right
        sourceHeight = imgHeight;
        sourceWidth = imgHeight / targetRatio;
        sourceX = (imgWidth - sourceWidth) / 2; // Center the crop horizontally
        sourceY = 0;
      }
      
      return { sourceX, sourceY, sourceWidth, sourceHeight };
    };
    
    // Draw all photos
    const drawPhotos = () => {
      // Draw each selected photo in its position
      for (let i = 0; i < 4; i++) {
        const imgSrc = i < selectedPhotos.length ? selectedPhotos[i] : null;
        const pos = photoPositions[i];
        
        // Calculate center position for the photo
        const centerX = canvas.width * pos.x;
        const centerY = canvas.height * pos.y;
        
        // Calculate top-left corner from center
        const destX = centerX - photoWidth / 2;
        const destY = centerY - photoHeight / 2;
        
        if (imgSrc) {
          const img = new Image();
          img.src = imgSrc;
          
          img.onload = () => {
            // Calculate crop dimensions to maintain aspect ratio of target area
            const { sourceX, sourceY, sourceWidth, sourceHeight } = calculateCropDimensions(
              img.naturalWidth, 
              img.naturalHeight, 
              photoWidth, 
              photoHeight
            );
            
            // Draw the cropped photo at the specified position
            ctx.drawImage(
              img,
              sourceX, sourceY,           // Source coordinates (where to start cropping)
              sourceWidth, sourceHeight,  // Source dimensions (how much to crop)
              destX, destY,               // Destination coordinates (where to place on canvas)
              photoWidth, photoHeight     // Destination dimensions (final size in frame)
            );
            
            // Redraw frame on top after each photo is drawn
            if (frameImg.complete && frameImg.naturalWidth !== 0) {
              ctx.drawImage(frameImg, 0, 0, canvas.width, canvas.height);
            }
          };
          
          img.onerror = () => {
            console.log(`Photo ${i + 1} failed to load`);
            drawPlaceholder(destX, destY, photoWidth, photoHeight);
          };
        } else {
          // Draw empty placeholder for this position
          drawPlaceholder(destX, destY, photoWidth, photoHeight);
        }
      }
    };
    
    // Helper function to draw placeholders
    const drawPlaceholder = (x, y, width, height) => {
      ctx.fillStyle = '#f0f0f0';
      ctx.fillRect(x, y, width, height);
      
      ctx.fillStyle = '#999';
      ctx.font = '24px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('', x + width/2, y + height/2);
    };
    
    // When frame successfully loads, draw it and then draw photos
    frameImg.onload = () => {
      console.log('Frame image loaded successfully');
      
      // First draw photos
      drawPhotos();
      
      // Then draw the frame on top of the photos
      setTimeout(() => {
        ctx.drawImage(frameImg, 0, 0, canvas.width, canvas.height);
      }, 100);
    };
  }, [selectedPhotos]);

  const goToResult = () => {
    navigate('/result', { state: { images: selectedPhotos } });
  };

  return (
    <div className="photo-selection-container">
      <div className="selection-content">
        {/* Left side: photo thumbnails in rows of 4 */}
        <div className="photo-grid">
        {allPhotos.map((photo, idx) => (
          <div key={idx} className="thumbnail-container">
            <img
              src={photo}
              alt={`Photo ${idx + 1}`}
              className={`photo-thumbnail ${selectedPhotos.includes(photo) ? 'selected' : ''}`}
              onClick={() => toggleSelect(photo)}
            />
            {selectedPhotos.includes(photo) && (
              <div className="photo-number">
                {selectedPhotos.indexOf(photo) + 1}
              </div>
            )}
          </div>
        ))}

        {/* 9th card for selection status */}
        <div className="thumbnail-container status-card">
          <div className="status-content">
            <div className="status-number">{selectedPhotos.length}/4</div>
            <div className="status-label">Selected</div>
          </div>
        </div>
      </div>

        {/* Right side: frame preview with canvas */}
        <div className="frame-preview-container">
          <div className="frame-preview">
            <canvas 
              ref={canvasRef} 
              className="photo-strip"
              style={{
                maxWidth: '450px',
                width: '100%',
                height: 'auto',
              }}
            />
          </div>  
          <button
            className="next-button"
            onClick={goToResult}
            disabled={selectedPhotos.length !== 4}
          >
            Create Photo Strip
          </button>
        </div>
      </div>
    </div>
  );
}

export default PhotoSelection;